import React, { useState } from "react";
import { Alert, Dialog, DialogContent, DialogTitle, FormControl, Grid, Link } from "@mui/material";
import { Duration } from "luxon";
import type { DateTime } from "luxon";
import { isDurationGreaterThan } from "utils/Validation/isDurationGreaterThan";
import { nameof } from "utils/nameof";
import {
  cloudSubscriptionOutageWindowFrequencyDescriptions,
  mapMaintenanceWindowDtoToRequest,
  type MaintenanceWindowDto,
} from "client/api/CloudSubscriptionApi";
import { CloudSubscriptionOutageWindowFrequency } from "client/api/CloudSubscriptionApi";
import { ErrorAlert } from "components/alert/ErrorAlert";
import { FormNonLocalizedTimePicker } from "components/form/FormNonLocalizedTimePicker";
import { FormSelect } from "components/form/FormSelect";
import { SubmittableForm } from "components/form/SubmittableForm";
import { timeZones } from "./timeZones";

type ChangeOutageWindowDialogProps = {
  cloudSubscriptionId: string;
  open: boolean;
  currentMaintenanceWindow: MaintenanceWindowDto;
  handleClose: () => void;
  submitChange: (
    outageWindowFrequencySelection: CloudSubscriptionOutageWindowFrequency,
    outageWindowTimeZoneSelection: string,
    outageWindowStartSelection: DateTime,
    outageWindowEndSelection: DateTime
  ) => Promise<void>;
};

type ChangeOutageWindowInputs = {
  outageWindowFrequency: CloudSubscriptionOutageWindowFrequency;
  outageWindowTimeZone: string;
  outageWindowStart: DateTime;
  outageWindowEnd: DateTime;
};

const baseTimeOptions = {
  required: {
    value: true,
    message: "Please enter or select a time.",
  },
  validate: {
    validDate: (v: DateTime) => v.isValid || "Please enter a valid time.",
  },
};

export function ChangeOutageWindowDialog(props: ChangeOutageWindowDialogProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | undefined>();

  const handleFormSubmit = async (inputs: ChangeOutageWindowInputs) => {
    await props.submitChange(
      inputs.outageWindowFrequency,
      inputs.outageWindowTimeZone,
      inputs.outageWindowStart,
      inputs.outageWindowEnd
    );
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        if (!loading) props.handleClose();
      }}
    >
      {error ? <ErrorAlert error={error} /> : null}
      <DialogTitle>Change Maintenance Window</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} rowSpacing={2}>
          <Grid size={12}>
            <h4>This feature is only intended to be used in agreement with the customer.</h4>
            <div>
              <i>
                Setting a Monthly Maintenance Window must be done in collaboration with{" "}
                <Link href={"https://octopusdeploy.slack.com/archives/C01Q95KPRM4"} target="_blank">
                  #cloud-platform-requests
                </Link>
                .
              </i>
            </div>
          </Grid>
          <Grid size={12}>
            <Alert severity="warning">A minimum of 2 hours is required.</Alert>
          </Grid>
          <Grid size={12}>
            <SubmittableForm
              mode="onChange"
              submitAction={handleFormSubmit}
              defaultValues={mapMaintenanceWindowDtoToRequest(props.currentMaintenanceWindow)}
            >
              {(formController) => {
                const { watch } = formController;
                const currentOutageWindowFrequency = watch("outageWindowFrequency");
                const currentOutageWindowTimeZone = watch("outageWindowTimeZone");
                const currentOutageWindowStart = watch("outageWindowStart");
                const currentOutageWindowEnd = watch("outageWindowEnd");

                return (
                  <>
                    <Grid size={6}>
                      <FormControl sx={{ marginTop: 4 }} fullWidth>
                        <FormSelect
                          name={nameof<ChangeOutageWindowInputs>("outageWindowFrequency")}
                          label="Frequency"
                          labelId="frequency-select-label"
                          rules={{
                            deps: [nameof<ChangeOutageWindowInputs>("outageWindowTimeZone")],
                            validate: {
                              monthlyFrequencyOrUtcTimeZone: (frequency: CloudSubscriptionOutageWindowFrequency) => {
                                return (
                                  frequency !== CloudSubscriptionOutageWindowFrequency.Daily ||
                                  currentOutageWindowTimeZone === "UTC" ||
                                  `Daily maintenance windows only support the UTC time zone.`
                                );
                              },
                            },
                          }}
                          selectItems={Object.entries(cloudSubscriptionOutageWindowFrequencyDescriptions)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid size={6}>
                      <FormControl sx={{ marginTop: 4 }} fullWidth>
                        <FormSelect
                          name={nameof<ChangeOutageWindowInputs>("outageWindowTimeZone")}
                          label="Time Zone"
                          labelId="time-zone-select-label"
                          rules={{
                            deps: [nameof<ChangeOutageWindowInputs>("outageWindowFrequency")],
                            validate: {
                              monthlyFrequencyOrUtcTimeZone: (timeZone: string) => {
                                return (
                                  currentOutageWindowFrequency !== CloudSubscriptionOutageWindowFrequency.Daily ||
                                  timeZone === "UTC" ||
                                  `Only the UTC time zone is supported for daily maintenance windows.`
                                );
                              },
                            },
                          }}
                          selectItems={timeZones.map((value) => [value, value])}
                        />
                      </FormControl>
                    </Grid>
                    <Grid size={6}>
                      <FormNonLocalizedTimePicker
                        label="Maintenance window start"
                        name={nameof<ChangeOutageWindowInputs>("outageWindowStart")}
                        defaultValue={props.currentMaintenanceWindow.startTime}
                        rules={{
                          ...baseTimeOptions,
                          deps: [nameof<ChangeOutageWindowInputs>("outageWindowEnd")],
                          validate: {
                            ...baseTimeOptions.validate,
                            lessThanEndTime: (v: DateTime) => {
                              return (
                                !currentOutageWindowEnd ||
                                isDurationGreaterThan(
                                  v,
                                  currentOutageWindowEnd,
                                  Duration.fromObject({
                                    hours: 1,
                                    minute: 59,
                                  })
                                ) ||
                                "Maintenance window start time should be at least 2 hours before the maintenance window ends."
                              );
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={6}>
                      <FormNonLocalizedTimePicker
                        label="Maintenance window end"
                        name={nameof<ChangeOutageWindowInputs>("outageWindowEnd")}
                        defaultValue={props.currentMaintenanceWindow.endTime}
                        rules={{
                          ...baseTimeOptions,
                          deps: [nameof<ChangeOutageWindowInputs>("outageWindowStart")],
                          validate: {
                            ...baseTimeOptions.validate,
                            greaterThanStartTime: (v: DateTime) => {
                              return (
                                !currentOutageWindowStart ||
                                isDurationGreaterThan(
                                  currentOutageWindowStart,
                                  v,
                                  Duration.fromObject({
                                    hours: 1,
                                    minutes: 59,
                                  })
                                ) ||
                                "Maintenance window end time should be at least 2 hours after the maintenance window starts."
                              );
                            },
                          },
                        }}
                      />
                    </Grid>
                  </>
                );
              }}
            </SubmittableForm>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
